import React, { useEffect, type FC } from 'react';
import { useParams } from 'react-router';
import { Redirect } from 'react-router-dom';

import { AccessControlCatalogReport } from './access_control/catalog_report';
import { AccessControlPermissionsAdmin } from './access_control/permissions_admin';
import { Edit as AccountAddressEdit } from './account/address/edit';
import { New as AccountAddressNew } from './account/address/new';
import { Show as AccountAddressShow } from './account/address/show';
import { AccountInventoryShow } from './accounts/inventory/show';
import { Edit as EditAccountPricing } from './accounts/pricing/edit';
import { Settings as AccountSettings } from './accounts/settings';
import { AccountShow } from './accounts/show/show';
import { AccountsTable } from './accounts/table';
import { AffiliateCreateForm } from './affiliates/create_form';
import { AffiliateDetails } from './affiliates/details';
import { AffiliatesList } from './affiliates/list';
import { AffiliateUpdateForm } from './affiliates/update_form';
import { AuctionsIndex } from './auctions/index';
import { AuctionUploadForm } from './auctions/uploads/form';
import { AvailabilityLaborAdjustmentForm } from './availability/availability_labor_adjustment/form';
import { List as AvailabilityLaborAdjustmentList } from './availability/availability_labor_adjustment/list';
import { GlobalUnavailabilityForm } from './availability/global_unavailability/form';
import { List as GlobalUnavailabilityList } from './availability/global_unavailability/list';
import { JobAvailabilityForm } from './availability/job_availability/form';
import { List as JobAvailabilityList } from './availability/job_availability/list';
import {
  WarehouseOperatingScheduleTemplateCreateForm,
  RegionOperatingScheduleTemplateCreateForm,
} from './availability/operating_schedules/template_create_form';
import { RescheduleOfferForm as AvailabilityRescheduleOffersForm } from './availability/reschedule_offers/form';
import { List as AvailabilityRescheduleOffersList } from './availability/reschedule_offers/list';
import { AvailabilityView } from './availability/view';
import { WarehouseCapacityForm } from './availability/warehouse_capacity/form';
import { List as WarehouseCapacityList } from './availability/warehouse_capacity/list';
import { Calendar } from './calendar/calendar';
import { ClaimDashboard, ClaimDetails } from './claims';
import { Collections } from './collections/index';
import { ContractorPasswordResetRequestApproveForm } from './contractor_password_reset_request/approve';
import { Dashboard as CouponsDashboard } from './coupons/dashboard';
import { Edit as CouponsEdit } from './coupons/edit';
import { New as CouponsNew } from './coupons/new';
import { Details as CustomerTicketDetails } from './customer_tickets/details';
import { Queue as CustomerTicketQueue } from './customer_tickets/queue';
import { EditCustomer } from './customers/edit';
import { DispatcherPage } from './dispatcher/dispatcher_page';
import { Edit as DispatchesEdit } from './dispatches/edit/edit';
import { DispatchesList } from './dispatches/list/list';
import { Show as DispatchesShow } from './dispatches/show/show';
import { EngineeringRoadmapPlanner } from './engineering_roadmap/planner';
import { CreateFacility } from './facilities/create_facility';
import { FacilityDetails } from './facilities/details';
import { FacilitiesList } from './facilities/list';
import { EditFacilityServiceType, FacilityServiceTypeDetails } from './facilities/service_type_details';
import {
  DepotSiteRedirect,
  RedirectToCreateFacility,
  RedirectToFacilitiesList,
  WarehouseSiteRedirect,
} from './facilities/site_catalog_redirects';
import { FeatureFlagsList } from './feature_flags/list';
import { GratuitiesDashboard } from './gratuities';
import { HiringInterviewTimeSlotsCalendar } from './hiring/interview_time_slots/calendar';
import { HiringJobApplications } from './hiring/job_applications/list';
import { HiringJobApplicationReview } from './hiring/job_applications/review';
import { HiringJobListingEdit } from './hiring/job_listings/edit';
import { HiringJobListings } from './hiring/job_listings/list';
import { HiringJobListingNew } from './hiring/job_listings/new';
import { HiringJobEdit } from './hiring/jobs/edit';
import { HiringJobList } from './hiring/jobs/list';
import { HiringJobNew } from './hiring/jobs/new';
import { HiringQuestionEdit } from './hiring/questions/edit';
import { HiringQuestionList } from './hiring/questions/list';
import { HiringQuestionNew } from './hiring/questions/new';
import { HRSLocationsList } from './hrs/locations/list';
import { HRSProfileDefaultsList } from './hrs/profile_defaults/list';
import { HRSRoleDefaultsList } from './hrs/role_defaults/list';
import { IngestionUploadForm } from './ingestion/uploads/form';
import { List as IngestionUploadsList } from './ingestion/uploads/list';
import { ItemCategoryManagement } from './item_category_management/index';
import { NewItemGroup } from './item_groups/new_item_group';
import { BulkCategorize } from './items/bulk_categorize';
import { ItemDetails } from './items/item_details';
import { ItemsExplorerContent } from './items_explorer';
import { Details as LandingLocationCityDetails } from './landing/location/city/details';
import { LandingLocationCityForm } from './landing/location/city/form';
import { List as LandingLocationCityList } from './landing/location/city/list';
import { Details as LandingLocationContentDetails } from './landing/location/content/details';
import { LandingLocationContentForm } from './landing/location/content/form';
import { Details as LandingLocationNeighborhoodDetails } from './landing/location/neighborhood/details';
import { LandingLocationNeighborhoodForm } from './landing/location/neighborhood/form';
import { List as LandingLocationNeighborhoodList } from './landing/location/neighborhood/list';
import { LandingLocationPageDetails } from './landing/location/page/details';
import { LandingLocationPageForm } from './landing/location/page/form';
import { LandingLocationPageList } from './landing/location/page/list';
import { Details as LandingLocationRouteDetails } from './landing/location/route/details';
import { LandingLocationRouteForm } from './landing/location/route/form';
import { List as LandingLocationRouteList } from './landing/location/route/list';
import { LandingLocationStateForm } from './landing/location/state/form';
import { List as LandingLocationStateList } from './landing/location/state/list';
import { LeadEditPage } from './leads/edit_page';
import { List as LeadsList } from './leads/list';
import { LocationDetails } from './locations/details';
import { LocationEdit } from './locations/edit';
import { LocationList } from './locations/list';
import { LocationNew } from './locations/new';
import { LogisticsNewAccount } from './logistics/accounts/new';
import { LogisticsDetailedLoad } from './logistics/loads/detailed';
import { LogisticsEditLoad } from './logistics/loads/edit';
import { LogisticsLoads } from './logistics/loads/list';
import { LogisticsNewLoad } from './logistics/loads/new';
import { LogisticsEditPurchaseOrder } from './logistics/purchase_orders/edit';
import { LogisticsPurchaseOrders } from './logistics/purchase_orders/list';
import { LogisticsNewPurchaseOrder } from './logistics/purchase_orders/new';
import { LogisticsDetailedSku } from './logistics/skus/detailed';
import { LogisticsEditSku } from './logistics/skus/edit';
import { LogisticsNewSku } from './logistics/skus/new';
import { IterableExportsList } from './marketing/iterable_exports_list';
import { SmsInbox } from './messages/inbox';
import { ManageFollowUps } from './moving/follow_ups/manage';
import { Planner as NightTransportPlanner } from './night_transport/planner/planner';
import { NotesUploader } from './notes_uploader/notes_uploader';
import { OpsAuditAudit } from './ops_audit/audit/audit';
import { OpsAuditAuditReview } from './ops_audit/audit_review/audit_review';
import { OpsAuditConfigurations } from './ops_audit/configurations';
import { OpsAuditEditConfiguration } from './ops_audit/configurations/edit';
import { OpsMap } from './ops_map';
import {
  OrderMoveForm,
  OrderPickupNew,
  OrderPickupEdit,
  OrderReturnNew,
  OrderReturnEdit,
  OrderRetailDeliveryForm,
} from './orders/form/order_form';
import { OrderReschedule } from './orders/order_reschedule';
import { OrderRedirect } from './orders/redirect';
import { OrderShipForm } from './orders/ship_form';
import { OrderShow } from './orders/show';
import { OrdersTable } from './orders/table';
import { TaskOrderCreate } from './orders/task_orders/task_order_create';
import { TaskOrderEdit } from './orders/task_orders/task_order_edit';
import { TestOrders } from './orders/test_orders';
import { PalletRequestsList } from './pallet_requests/pallet_requests_list';
import { CreatePalletType } from './pallet_types/create_pallet_type';
import { EditPalletType } from './pallet_types/edit_pallet_type';
import { PalletTypes } from './pallet_types/pallet_types';
import { PalletDetails } from './pallets/details';
import { PartnershipsAdmin } from './partnerships/partners_admin';
import { Details as PaymentPlanDetails } from './payment_plans/details';
import { New as PaymentPlanNew } from './payment_plans/new';
import { PhoneCallsIndex } from './phone_calls/index';
import { PhoneCallsShow } from './phone_calls/show';
import { PricingUploadForm } from './pricing/uploads/form';
import { List as PricingUploadsList } from './pricing/uploads/list';
import { PTORequestsList } from './pto/requests/list';
import { QrCodePanel } from './qa_automation/qr_code_panel';
import { DoorToDoorRateAdjustmentUploaderForm } from './rate_adjustment/uploader/door_to_door_form';
import { SelfStorageRateAdjustmentUploaderForm } from './rate_adjustment/uploader/self_storage_form';
import { RedirectEdit } from './redirect/redirect_edit';
import { RedirectList } from './redirect/redirect_list';
import { RedirectNew } from './redirect/redirect_new';
import { CreateReferralForm } from './referrals/create_form';
import { ReferralsList } from './referrals/list';
import { Dashboard as RegionsDashboard } from './regions/dashboard';
import { Details as RegionDetails } from './regions/details';
import { Edit as RegionEdit } from './regions/edit';
import { List as SelfStorageFacilitiesList } from './self_storage/facilities/list';
import { SelfStorageUnits } from './self_storage/units';
import { Detail as SelfStorageUnitDetail } from './self_storage/units/detail';
import { ShiftsIndex } from './shifts/index';
import { SopDocumentList } from './sop_documents/list';
import { TeamsList } from './teams/list';
import { ThirdPartyTransferCreate } from './third_party_transfers/create';
import { ThirdPartyTransferDetails } from './third_party_transfers/details';
import { ThirdPartyTransferUpdate } from './third_party_transfers/update';
import { TicketManagement } from './ticket_management';
import { Queue as HomeTicketQueue } from './tickets/home';
import { NewTicket } from './tickets/new_ticket';
import { TicketDetails } from './tickets/ticket_details';
import { TrainingUploadAttestationsList } from './training_upload/attestations/list';
import { TrainingUploadForm } from './training_upload/form';
import { TrainingUploadList } from './training_upload/list';
import { TransferPoolEdit } from './transfer_pools/edit';
import { TransferPoolList } from './transfer_pools/list';
import { TransferPoolShow } from './transfer_pools/show';
import { TransferablesIndex } from './transferables/index';
import { TransferDetails } from './transfers/details';
import { TransferEdit } from './transfers/edit';
import { TransferList } from './transfers/list';
import type { RouteState } from './types';
import { UserSkillsReport } from './user_skills/report';
import { Details as UserDetails } from './users/details';
import { UserUpdateForm } from './users/form';
import { UserCreateForm } from './users/form';
import { List as UsersList } from './users/list';
import { UtmPhoneNew } from './utm_phones/new';
import { VehicleRouteMatcher } from './vehicle_route_matcher/vehicle_route_matcher';
import { CreateVehicleType } from './vehicle_types/create_vehicle_type';
import { EditVehicleType } from './vehicle_types/edit_vehicle_type';
import { VehicleTypes } from './vehicle_types/vehicle_types';
import { VehicleDetails } from './vehicles/details';
import { VehicleEdit } from './vehicles/edit';
import { VehicleList } from './vehicles/list';
import { VehicleNew } from './vehicles/new';
import { VoicemailsDetails } from './voicemails/details';
import { VoicemailsList } from './voicemails/list';
import { WarehouseTaskCreate } from './warehouse_tasks/create';
import { WarehouseTasksList } from './warehouse_tasks/index';
import { WarehouseTaskShow } from './warehouse_tasks/show';
import { ZendeskUpload } from './zendesk/zendesk_upload';

const LegacyTimeClockRedirect: FC = () => {
  useEffect(() => {
    location.replace('/timetable/time_clocks');
  }, []);
  return null;
};

const LegacySubscribeRedirect: FC = () => {
  const { accountID } = useParams<{ accountID: string }>();
  return <Redirect to={`/accounts/${accountID}/pricing/edit`} />;
};

const LegacyShowCouponRedirect: FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Redirect to={`/marketing/coupons/${id}/edit`} />;
};

const LegacyYieldManagementRedirect: FC = () => {
  return <Redirect to="/door_to_door/rate_adjustments" />;
};

type Route = {
  component: React.ComponentType;
  state: RouteState;
  path: string;
};

const route = (name: RouteState, options: Omit<Route, 'state'>): Route => ({
  state: name,
  ...options,
});

export const ROUTES: Route[] = [
  route('inbox', {
    path: '/',
    component: HomeTicketQueue,
  }),
  route('newTicket', {
    path: '/tickets/new',
    component: NewTicket,
  }),
  route('viewTicket', {
    path: '/tickets/:ticketID',
    component: TicketDetails,
  }),
  route('engineeringRoadmap', {
    path: '/engineering_roadmap',
    component: EngineeringRoadmapPlanner,
  }),
  route('ticketmanagement', {
    path: '/ticket_management',
    component: TicketManagement,
  }),
  route('manageItemCategories', {
    path: '/item_category_management',
    component: ItemCategoryManagement,
  }),
  route('map', {
    path: '/map',
    component: OpsMap,
  }),
  route('collections', {
    path: '/collections',
    component: Collections,
  }),
  route('smsInbox', {
    path: '/smsinbox',
    component: SmsInbox,
  }),
  route('phoneCalls', {
    path: '/phone_calls',
    component: PhoneCallsIndex,
  }),
  route('viewPhoneCall', {
    path: '/phone_calls/:id',
    component: PhoneCallsShow,
  }),
  route('shifts', {
    path: '/shifts',
    component: ShiftsIndex,
  }),
  route('itemsExplorer', {
    path: '/items',
    component: ItemsExplorerContent,
  }),
  route('availability', {
    path: '/availability',
    component: AvailabilityView,
  }),
  route('ptoRequests', {
    path: '/pto_requests',
    component: PTORequestsList,
  }),
  route('dispatcher', {
    path: '/dispatcher',
    component: DispatcherPage,
  }),
  route('dispatches', {
    path: '/dispatches',
    component: DispatchesList,
  }),
  route('viewDispatch', {
    path: '/dispatches/:dispatchID',
    component: DispatchesShow,
  }),
  route('editDispatch', {
    path: '/dispatches/:dispatchID/edit',
    component: DispatchesEdit,
  }),
  route('accounts', {
    path: '/accounts',
    component: AccountsTable,
  }),
  route('viewAccount', {
    path: '/accounts/:accountID',
    component: AccountShow,
  }),
  route('viewCategorizables', {
    path: '/accounts/:accountID/items/categorizables',
    component: BulkCategorize,
  }),
  route('viewItem', {
    path: '/accounts/:accountID/items/:id',
    component: ItemDetails,
  }),
  route('newReturn', {
    path: '/accounts/:accountID/returns/new',
    component: OrderReturnNew,
  }),
  route('editReturn', {
    path: '/accounts/:accountID/returns/:orderID/edit',
    component: OrderReturnEdit,
  }),
  route('newPickup', {
    path: '/accounts/:accountID/pickups/new',
    component: OrderPickupNew,
  }),
  route('editPickup', {
    path: '/accounts/:accountID/pickups/:orderID/edit',
    component: OrderPickupEdit,
  }),
  route('editMove', {
    path: '/accounts/:accountID/moves/:orderID/edit',
    component: OrderMoveForm,
  }),
  route('taskOrderCreate', {
    path: '/accounts/:accountID/task_orders/new',
    component: TaskOrderCreate,
  }),
  route('taskOrderUpdate', {
    path: '/accounts/:accountID/task_orders/:orderID/edit',
    component: TaskOrderEdit,
  }),
  route('inventory', {
    path: '/accounts/:accountID/inventory',
    component: AccountInventoryShow,
  }),
  route('newAccountItemGroup', {
    path: '/accounts/:accountID/item_groups/new',
    component: NewItemGroup,
  }),
  route('editCustomer', {
    path: '/accounts/:accountID/customers/:id/edit',
    component: EditCustomer,
  }),
  route('editPreferences', {
    path: '/accounts/:accountID/preferences/edit',
    component: AccountSettings,
  }),
  route('newSubscribe', {
    path: '/accounts/:accountID/subscribes/new',
    component: LegacySubscribeRedirect,
  }),
  route('newAddress', {
    path: '/accounts/:accountID/addresses/new',
    component: AccountAddressNew,
  }),
  route('viewAddress', {
    path: '/accounts/:accountID/addresses/:addressID',
    component: AccountAddressShow,
  }),
  route('editAddress', {
    path: '/accounts/:accountID/addresses/:addressID/edit',
    component: AccountAddressEdit,
  }),
  route('viewPallet', {
    path: '/pallets/:id',
    component: PalletDetails,
  }),
  route('calendar', {
    path: '/calendar',
    component: Calendar,
  }),
  route('orders', {
    path: '/orders',
    component: OrdersTable,
  }),
  route('redirectOrder', {
    path: '/orders/:orderID',
    component: OrderRedirect,
  }),
  route('viewOrder', {
    path: '/accounts/:accountID/orders/:orderID',
    component: OrderShow,
  }),
  route('rescheduleOrder', {
    path: '/accounts/:accountID/orders/:orderID/reschedule',
    component: OrderReschedule,
  }),
  route('newShip', {
    path: '/accounts/:accountID/orders/:orderID/ship/new',
    component: OrderShipForm,
  }),
  route('newFollowUp', {
    path: '/accounts/:accountID/moves/:orderID/follow_up/new',
    component: ManageFollowUps,
  }),
  route('warehouses', {
    path: '/warehouses',
    component: RedirectToFacilitiesList,
  }),
  route('facilities', {
    path: '/facilities',
    component: FacilitiesList,
  }),
  route('createFacility', {
    path: '/facilities/new',
    component: CreateFacility,
  }),
  route('editFacilityServiceType', {
    path: '/facilities/service_types/:id/edit',
    component: EditFacilityServiceType,
  }),
  route('viewFacilityServiceType', {
    path: '/facilities/service_types/:id',
    component: FacilityServiceTypeDetails,
  }),
  route('viewFacility', {
    path: '/facilities/:id',
    component: FacilityDetails,
  }),
  route('createWarehouse', {
    path: '/warehouses/new',
    component: RedirectToCreateFacility,
  }),
  route('palletRequestsList', {
    path: '/warehouses/:warehouseID/pallet_requests',
    component: PalletRequestsList,
  }),
  route('viewWarehouse', {
    path: '/warehouses/:id',
    component: WarehouseSiteRedirect,
  }),
  route('locations', {
    path: '/warehouses/:warehouseID/locations',
    component: LocationList,
  }),
  route('listTransferPool', {
    path: '/transferpools',
    component: TransferPoolList,
  }),
  route('viewTransferPool', {
    path: '/transferpools/:id',
    component: TransferPoolShow,
  }),
  route('editTransferPool', {
    path: '/transferpools/:id/edit',
    component: TransferPoolEdit,
  }),
  route('transfers', {
    path: '/transfers',
    component: TransferList,
  }),
  route('editTransfer', {
    path: '/transfers/:id/edit',
    component: TransferEdit,
  }),
  route('viewTransfer', {
    path: '/transfers/:id',
    component: TransferDetails,
  }),
  route('transferables', {
    path: '/transferables',
    component: TransferablesIndex,
  }),
  route('newLocation', {
    path: '/warehouses/:warehouseID/locations/new',
    component: LocationNew,
  }),
  route('viewLocation', {
    path: '/locations/:id',
    component: LocationDetails,
  }),
  route('editLocation', {
    path: '/locations/:id/edit',
    component: LocationEdit,
  }),
  route('users', {
    path: '/users',
    component: UsersList,
  }),
  route('newUser', {
    path: '/users/new',
    component: UserCreateForm,
  }),
  route('viewUser', {
    path: '/users/:id',
    component: UserDetails,
  }),
  route('editUser', {
    path: '/users/:id/edit',
    component: UserUpdateForm,
  }),
  route('vehicles', {
    path: '/vehicles',
    component: VehicleList,
  }),
  route('newVehicle', {
    path: '/vehicles/new',
    component: VehicleNew,
  }),
  route('showVehicle', {
    path: '/vehicles/:id',
    component: VehicleDetails,
  }),
  route('editVehicle', {
    path: '/vehicles/:id/edit',
    component: VehicleEdit,
  }),
  route('voicemails', {
    path: '/voicemails',
    component: VoicemailsList,
  }),
  route('viewVoicemail', {
    path: '/voicemails/:id',
    component: VoicemailsDetails,
  }),
  route('leads', {
    path: '/leads',
    component: LeadsList,
  }),
  route('editLead', {
    path: '/leads/:id/edit',
    component: LeadEditPage,
  }),
  route('redirects', {
    path: '/marketing/redirects',
    component: RedirectList,
  }),
  route('newRedirect', {
    path: '/marketing/redirects/new',
    component: RedirectNew,
  }),
  route('editRedirect', {
    path: '/marketing/redirects/:id/edit',
    component: RedirectEdit,
  }),
  route('affiliates', {
    path: '/marketing/affiliates',
    component: AffiliatesList,
  }),
  route('newAffiliate', {
    path: '/marketing/affiliates/new',
    component: AffiliateCreateForm,
  }),
  route('editAffiliate', {
    path: '/marketing/affiliates/:id/edit',
    component: AffiliateUpdateForm,
  }),
  route('showAffiliate', {
    path: '/marketing/affiliates/:id',
    component: AffiliateDetails,
  }),
  route('coupons', {
    path: '/marketing/coupons',
    component: CouponsDashboard,
  }),
  route('newCoupon', {
    path: '/marketing/coupons/new',
    component: CouponsNew,
  }),
  route('showCoupon', {
    path: '/marketing/coupons/:id',
    component: LegacyShowCouponRedirect,
  }),
  route('editCoupon', {
    path: '/marketing/coupons/:id/edit',
    component: CouponsEdit,
  }),
  route('referrals', {
    path: '/marketing/referrals',
    component: ReferralsList,
  }),
  route('newReferral', {
    path: '/marketing/referrals/new',
    component: CreateReferralForm,
  }),
  route('utmPhones', {
    path: '/marketing/utm_phones/new',
    component: UtmPhoneNew,
  }),
  route('qrCodeGenerator', {
    path: '/marketing/qr_codes',
    component: QrCodePanel,
  }),
  route('iterableExports', {
    path: '/marketing/email_exports',
    component: IterableExportsList,
  }),
  route('newPricingUpload', {
    path: '/pricing_uploads/new',
    component: PricingUploadForm,
  }),
  route('pricingUploads', {
    path: '/pricing_uploads',
    component: PricingUploadsList,
  }),
  route('newIngestion', {
    path: '/ingestions/new',
    component: IngestionUploadForm,
  }),
  route('ingestions', {
    path: '/ingestions',
    component: IngestionUploadsList,
  }),
  route('doorToDoorRateAdjustments', {
    path: '/door_to_door/rate_adjustments',
    component: DoorToDoorRateAdjustmentUploaderForm,
  }),
  route('yieldManagement', {
    path: '/yield_management',
    component: LegacyYieldManagementRedirect,
  }),
  route('hrsRoleDefaults', {
    path: '/hrs_role_defaults',
    component: HRSRoleDefaultsList,
  }),
  route('hrsProfileDefaults', {
    path: '/hrs_profile_defaults',
    component: HRSProfileDefaultsList,
  }),
  route('hrsLocations', {
    path: '/hrs_locations',
    component: HRSLocationsList,
  }),
  route('featureFlags', {
    path: '/feature_flags',
    component: FeatureFlagsList,
  }),
  route('teams', {
    path: '/teams',
    component: TeamsList,
  }),
  route('accessControlCatalog', {
    path: '/access_control/catalog',
    component: AccessControlCatalogReport,
  }),
  route('accessControlPermissions', {
    path: '/access_control/permissions',
    component: AccessControlPermissionsAdmin,
  }),
  route('userSkills', {
    path: '/user_skills',
    component: UserSkillsReport,
  }),
  route('partnershipsPartners', {
    path: '/partnerships',
    component: PartnershipsAdmin,
  }),
  route('hiringCandidates', {
    path: '/hiring/candidates',
    component: HiringJobApplications,
  }),
  route('hiringJobApplicationReview', {
    path: '/hiring/job_applications/:id/review',
    component: HiringJobApplicationReview,
  }),
  route('hiringRoles', {
    path: '/hiring/roles',
    component: HiringJobList,
  }),
  route('newHiringRole', {
    path: '/hiring/roles/new',
    component: HiringJobNew,
  }),
  route('editHiringRole', {
    path: '/hiring/roles/:id/edit',
    component: HiringJobEdit,
  }),
  route('hiringJobListings', {
    path: '/hiring/job_listings',
    component: HiringJobListings,
  }),
  route('newHiringJobListing', {
    path: '/hiring/job_listings/new',
    component: HiringJobListingNew,
  }),
  route('editHiringJobListing', {
    path: '/hiring/job_listings/:id/edit',
    component: HiringJobListingEdit,
  }),
  route('hiringInterviewTimeSlots', {
    path: '/hiring/interview_time_slots',
    component: HiringInterviewTimeSlotsCalendar,
  }),
  route('hiringPreScreenQuestions', {
    path: '/hiring/pre_screen_questions',
    component: HiringQuestionList,
  }),
  route('newHiringPreScreenQuestion', {
    path: '/hiring/pre_screen_questions/new',
    component: HiringQuestionNew,
  }),
  route('editHiringPreScreenQuestion', {
    path: '/hiring/pre_screen_questions/:id/edit',
    component: HiringQuestionEdit,
  }),
  route('trainingUploads', {
    path: '/training_uploads',
    component: TrainingUploadList,
  }),
  route('trainingUploadNew', {
    path: '/training_uploads/new',
    component: TrainingUploadForm,
  }),
  route('trainingUploadEdit', {
    path: '/training_uploads/:id/edit',
    component: TrainingUploadForm,
  }),
  route('trainingUploadAttestations', {
    path: '/training_uploads/:id/attestations',
    component: TrainingUploadAttestationsList,
  }),
  route('sopDocuments', {
    path: '/sop_documents',
    component: SopDocumentList,
  }),
  route('selfStorageFacilities', {
    path: '/self_storage/facilities',
    component: SelfStorageFacilitiesList,
  }),
  route('selfStorageUnitsIndex', {
    path: '/self_storage/units',
    component: SelfStorageUnits,
  }),
  route('selfStorageUnitDetail', {
    path: '/self_storage/units/:id',
    component: SelfStorageUnitDetail,
  }),
  route('selfStorageRateAdjustmentUploader', {
    path: '/self_storage/rate_adjustments',
    component: SelfStorageRateAdjustmentUploaderForm,
  }),
  route('timeClock', {
    path: '/timetable/time_clocks',
    component: LegacyTimeClockRedirect,
  }),
  route('gratuities', {
    path: '/gratuities',
    component: GratuitiesDashboard,
  }),
  route('vehicleRouteMatcher', {
    path: '/vehicle_route_matcher',
    component: VehicleRouteMatcher,
  }),
  route('claims', {
    path: '/claims',
    component: ClaimDashboard,
  }),
  route('claim', {
    path: '/claims/:id',
    component: ClaimDetails,
  }),
  route('viewCustomerTicket', {
    path: '/customer_tickets/:id',
    component: CustomerTicketDetails,
  }),
  route('customerTickets', {
    path: '/customer_tickets',
    component: CustomerTicketQueue,
  }),
  route('newPaymentPlan', {
    path: '/accounts/:accountID/payment_plans/new',
    component: PaymentPlanNew,
  }),
  route('paymentPlan', {
    path: '/accounts/:accountID/payment_plans/:offerID',
    component: PaymentPlanDetails,
  }),
  route('newVehicleType', {
    path: '/vehicle_types/new',
    component: CreateVehicleType,
  }),
  route('vehicleTypes', {
    path: '/vehicle_types',
    component: VehicleTypes,
  }),
  route('editVehicleType', {
    path: '/vehicle_types/:id/edit',
    component: EditVehicleType,
  }),
  route('depotsList', {
    path: '/depots',
    component: RedirectToFacilitiesList,
  }),
  route('createDepot', {
    path: '/depots/new',
    component: RedirectToCreateFacility,
  }),
  route('viewDepot', {
    path: '/depots/:id',
    component: DepotSiteRedirect,
  }),
  route('regions', {
    path: '/regions',
    component: RegionsDashboard,
  }),
  route('region', {
    path: '/regions/:id',
    component: RegionDetails,
  }),
  route('editRegion', {
    path: '/regions/:id/edit',
    component: RegionEdit,
  }),
  route('palletTypes', {
    path: '/pallet_types',
    component: PalletTypes,
  }),
  route('createPalletTypes', {
    path: '/pallet_types/new',
    component: CreatePalletType,
  }),
  route('editPalletTypes', {
    path: '/pallet_types/:id/edit',
    component: EditPalletType,
  }),
  route('notesUploader', {
    path: '/notes_uploader',
    component: NotesUploader,
  }),
  route('opsAuditConfigurations', {
    path: '/ops_audit/configurations',
    component: OpsAuditConfigurations,
  }),
  route('opsAuditAuditReview', {
    path: '/ops_audit/configurations/:configurationID/audit_review',
    component: OpsAuditAuditReview,
  }),
  route('opsAuditAudit', {
    path: '/ops_audit/audits/:auditID',
    component: OpsAuditAudit,
  }),
  route('loads', {
    path: '/logistics/loads',
    component: LogisticsLoads,
  }),
  route('createLoad', {
    path: '/logistics/loads/new',
    component: LogisticsNewLoad,
  }),
  route('editLoad', {
    path: '/logistics/loads/:id/edit',
    component: LogisticsEditLoad,
  }),
  route('detailedLoad', {
    path: '/logistics/loads/:id',
    component: LogisticsDetailedLoad,
  }),
  route('purchaseOrders', {
    path: '/logistics/purchase_orders',
    component: LogisticsPurchaseOrders,
  }),
  route('newPurchaseOrder', {
    path: '/logistics/purchase_orders/new',
    component: LogisticsNewPurchaseOrder,
  }),
  route('editPurchaseOrder', {
    path: '/logistics/purchase_orders/:id/edit',
    component: LogisticsEditPurchaseOrder,
  }),
  route('newSku', {
    path: '/logistics/skus/new',
    component: LogisticsNewSku,
  }),
  route('editSku', {
    path: '/logistics/skus/:id/edit',
    component: LogisticsEditSku,
  }),
  route('detailedSku', {
    path: '/logistics/skus/:id',
    component: LogisticsDetailedSku,
  }),
  route('qaQrCodes', {
    path: '/qaQrCodes',
    component: QrCodePanel,
  }),
  route('testOrders', {
    path: '/test_orders',
    component: TestOrders,
  }),
  route('editAccountPricing', {
    path: '/accounts/:accountID/pricing/edit',
    component: EditAccountPricing,
  }),
  route('createLogisticsAccount', {
    path: '/logistics/accounts/new',
    component: LogisticsNewAccount,
  }),
  route('editRetailDelivery', {
    path: '/accounts/:accountID/retail_deliveries/:orderID/edit',
    component: OrderRetailDeliveryForm,
  }),
  route('opsAuditEditConfiguration', {
    path: '/ops_audit/configurations/:id/edit',
    component: OpsAuditEditConfiguration,
  }),
  route('nightTransportPlanner', {
    path: '/night_transport/planner',
    component: NightTransportPlanner,
  }),
  route('landingLocationPageList', {
    path: '/landing/location/pages',
    component: LandingLocationPageList,
  }),
  route('landingLocationPageDetails', {
    path: '/landing/location/pages/:id',
    component: LandingLocationPageDetails,
  }),
  route('newLandingLocationPage', {
    path: '/landing/location/pages/new',
    component: LandingLocationPageForm,
  }),
  route('editLandingLocationPage', {
    path: '/landing/location/pages/:id/edit',
    component: LandingLocationPageForm,
  }),
  route('landingLocationStateList', {
    path: '/landing/location/states',
    component: LandingLocationStateList,
  }),
  route('newLandingLocationState', {
    path: '/landing/location/states/new',
    component: LandingLocationStateForm,
  }),
  route('editLandingLocationState', {
    path: '/landing/location/states/:id/edit',
    component: LandingLocationStateForm,
  }),
  route('landingLocationCityList', {
    path: '/landing/location/cities',
    component: LandingLocationCityList,
  }),
  route('newLandingLocationCity', {
    path: '/landing/location/cities/new',
    component: LandingLocationCityForm,
  }),
  route('editLandingLocationCity', {
    path: '/landing/location/cities/:id/edit',
    component: LandingLocationCityForm,
  }),
  route('landingLocationCityDetails', {
    path: '/landing/location/cities/:id',
    component: LandingLocationCityDetails,
  }),
  route('landingLocationNeighborhoodList', {
    path: '/landing/location/neighborhoods',
    component: LandingLocationNeighborhoodList,
  }),
  route('newLandingLocationNeighborhood', {
    path: '/landing/location/neighborhoods/new',
    component: LandingLocationNeighborhoodForm,
  }),
  route('editLandingLocationNeighborhood', {
    path: '/landing/location/neighborhoods/:id/edit',
    component: LandingLocationNeighborhoodForm,
  }),
  route('landingLocationNeighborhood', {
    path: '/landing/location/neighborhoods/:id',
    component: LandingLocationNeighborhoodDetails,
  }),
  route('landingLocationRouteList', {
    path: '/landing/location/routes',
    component: LandingLocationRouteList,
  }),
  route('newLandingLocationRoute', {
    path: '/landing/location/routes/new',
    component: LandingLocationRouteForm,
  }),
  route('editLandingLocationRoute', {
    path: '/landing/location/routes/:id/edit',
    component: LandingLocationRouteForm,
  }),
  route('landingLocationRoute', {
    path: '/landing/location/routes/:id',
    component: LandingLocationRouteDetails,
  }),
  route('newLandingLocationContent', {
    path: '/landing/location/contents/new',
    component: LandingLocationContentForm,
  }),
  route('editLandingLocationContent', {
    path: '/landing/location/contents/:id/edit',
    component: LandingLocationContentForm,
  }),
  route('landingLocationContent', {
    path: '/landing/location/contents/:id',
    component: LandingLocationContentDetails,
  }),
  route('globalUnavailability', {
    path: '/global_unavailability',
    component: GlobalUnavailabilityList,
  }),
  route('globalUnavailabilityNew', {
    path: '/global_unavailability/new',
    component: GlobalUnavailabilityForm,
  }),
  route('jobAvailability', {
    path: '/job_availability',
    component: JobAvailabilityList,
  }),
  route('jobAvailabilityNew', {
    path: '/job_availability/new',
    component: JobAvailabilityForm,
  }),
  route('warehouseCapacity', {
    path: '/warehouse_capacity',
    component: WarehouseCapacityList,
  }),
  route('warehouseCapacityNew', {
    path: '/warehouse_capacity/new',
    component: WarehouseCapacityForm,
  }),
  route('warehouseOperatingScheduleTemplateNew', {
    path: '/warehouses/operating_schedule_templates/new',
    component: WarehouseOperatingScheduleTemplateCreateForm,
  }),
  route('regionOperatingScheduleTemplateNew', {
    path: '/regions/operating_schedule_templates/new',
    component: RegionOperatingScheduleTemplateCreateForm,
  }),
  route('availabilityLaborAdjustment', {
    path: '/availability_labor_adjustment',
    component: AvailabilityLaborAdjustmentList,
  }),
  route('availabilityLaborAdjustmentNew', {
    path: '/availability_labor_adjustment/new',
    component: AvailabilityLaborAdjustmentForm,
  }),
  route('availabilityRescheduleOffers', {
    path: '/availability_reschedule_offers',
    component: AvailabilityRescheduleOffersList,
  }),
  route('availabilityRescheduleOffersNew', {
    path: '/availability_reschedule_offers/new',
    component: AvailabilityRescheduleOffersForm,
  }),
  route('auctions', {
    path: '/auctions',
    component: AuctionsIndex,
  }),
  route('auctionUploadNew', {
    path: '/auctions/new',
    component: AuctionUploadForm,
  }),
  route('zendeskUploads', {
    path: '/zendesk/uploads',
    component: ZendeskUpload,
  }),
  route('contractorPasswordResetRequestApprove', {
    path: '/contractor_password_reset_requests/:mfaRequestID',
    component: ContractorPasswordResetRequestApproveForm,
  }),
  route('warehouseTasks', {
    path: '/warehouse_tasks',
    component: WarehouseTasksList,
  }),
  route('warehouseTaskNew', {
    path: '/warehouse_tasks/new',
    component: WarehouseTaskCreate,
  }),
  route('warehouseTaskShow', {
    path: '/warehouse_tasks/:id',
    component: WarehouseTaskShow,
  }),
  route('thirdPartyTransferCreate', {
    path: '/accounts/:accountID/third_party_transfers/new',
    component: ThirdPartyTransferCreate,
  }),
  route('thirdPartyTransferUpdate', {
    path: '/accounts/:accountID/third_party_transfers/:thirdPartyTransferID/edit',
    component: ThirdPartyTransferUpdate,
  }),
  route('thirdPartyTransfer', {
    path: '/accounts/:accountID/third_party_transfers/:thirdPartyTransferID',
    component: ThirdPartyTransferDetails,
  }),
];
